import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MyHeader from "../../components/common/MyHeader";
import ArtifactList from "./ArtifactList";
import { useAuth } from "../../contexts/AuthContext";
import { getTeamAllSessions } from "../../firebase";
import PRPopupCard from './PRPopupCard';
import GoogleDocPopupCard from './GoogleDocPopupCard';
import FigmaPopupCard from './FigmaPopupCard';
import FilePopupCard from './FilePopupCard';
import DiscordDraftSection from '../../components/AddActivityWindow/DiscordDraftSection';
import PendingRatingsAlert from '../../components/PendingRatingsAlert';

const Artifacts = ({ isAdminView = false }) => {
    const { groupInfo, groupMembers } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [activePR, setActivePR] = useState(null);
    const [activeDoc, setActiveDoc] = useState(null);
    const [activeFigma, setActiveFigma] = useState(null);
    const [activeDiscord, setActiveDiscord] = useState(null);
    const [activeFile, setActiveFile] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        async function fetchSessions() {
            if (!groupInfo?.groupId) {
                return;
            }
            try {
                const groupId = groupInfo.groupId;
                const allSessions = await getTeamAllSessions(groupId);
                setSessions(allSessions);
            } catch (error) {
                console.error("Error fetching team sessions:", error);
            }
        }

        fetchSessions();
    }, [groupInfo]);

    return (
        <>
            <MyHeader
                alertComponent={!isAdminView ? <PendingRatingsAlert sessions={sessions} compact /> : null}
            />
            <PageWrapper>

                {sessions.length > 0 ? (
                    <ArtifactList sessions={sessions} setActivePR={setActivePR} setActiveDoc={setActiveDoc} setActiveFigma={setActiveFigma} setActiveDiscord={setActiveDiscord} setActiveFile={setActiveFile} groupMembers={groupMembers} />
                ) : (
                    <div>No session data</div>
                )}

                {activePR && (
                    <ModalOverlay onClick={() => setActivePR(null)}>
                        <ModalCard onClick={(e) => e.stopPropagation()}>
                            <PRPopupCard
                                groupMembers={groupMembers}
                                prLink={activePR.prLink}
                                title={activePR.title}
                                body={activePR.body}
                                commits={activePR.commits}
                                filesChanged={activePR.filesChanged}
                                codeChanges={activePR.codeChanges}
                                collaborators={activePR.collaborators}
                                coAuthors={activePR.coAuthors}
                                state={activePR.state}
                                onClose={() => setActivePR(null)}
                            />
                        </ModalCard>
                    </ModalOverlay>
                )}

                {activeDoc && (
                    <ModalOverlay onClick={() => setActiveDoc(null)}>
                        <ModalCard onClick={(e) => e.stopPropagation()}>
                            <GoogleDocPopupCard
                                meta={activeDoc}
                                groupMembers={groupMembers}
                                onClose={() => setActiveDoc(null)}
                            />
                        </ModalCard>
                    </ModalOverlay>
                )}

                {activeFigma && (
                    <ModalOverlay onClick={() => setActiveFigma(null)}>
                        <ModalCard onClick={(e) => e.stopPropagation()}>
                            <FigmaPopupCard
                                link={activeFigma.link}
                                meta={activeFigma.meta}
                                groupMembers={groupMembers}
                                onClose={() => setActiveFigma(null)}
                            />
                        </ModalCard>
                    </ModalOverlay>
                )}

                {activeFile && (
                    <ModalOverlay onClick={() => setActiveFile(null)}>
                        <ModalCard onClick={(e) => e.stopPropagation()}>
                            <FilePopupCard
                                name={activeFile.item.name}
                                url={activeFile.item.url}
                                onClose={() => setActiveFile(null)}
                            />
                        </ModalCard>
                    </ModalOverlay>
                )}

                {activeDiscord && (
                    <ModalOverlay onClick={() => setActiveDiscord(null)}>
                        <DiscordModalCard onClick={(e) => e.stopPropagation()}>
                            <DiscordModalHeader>
                                <DiscordModalTitle>Discord chat</DiscordModalTitle>
                                <DiscordCloseBtn type="button" onClick={() => setActiveDiscord(null)} aria-label="Close">
                                    ×
                                </DiscordCloseBtn>
                            </DiscordModalHeader>
                            <DiscordModalBody>
                                <DiscordDraftSection
                                    discordDraft={{ messages: activeDiscord.messages }}
                                    maxHeight={2000}
                                    selectable={false}
                                    groupMembers={groupMembers}
                                />
                            </DiscordModalBody>
                        </DiscordModalCard>
                    </ModalOverlay>
                )}
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #fff;
    display: flex;
    flex-direction: column;
    padding: 15px 20px 80px;
    min-height: 100vh;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
`;

const ModalCard = styled.div`
    width: 800px;
    max-width: 100%;
    background: white;
    padding: 24px;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    height: 80vh;             // 80% of screen height
    overflow-y: auto; 
`;

const DiscordModalCard = styled(ModalCard)`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 24px;
`;

const DiscordModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
`;

const DiscordModalTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
`;

const DiscordCloseBtn = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    line-height: 1;
    color: #64748b;
    cursor: pointer;
    padding: 0 4px;
    &:hover { color: #1e293b; }
`;

const DiscordModalBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    text-align: left;
`;

export default Artifacts;